import { Fragment, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconCheckboxIdle } from '@components/icons/checkbox-idle';
import { IconOpen } from '@components/icons/open';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';
import {
  isValidSsn,
  isOnlyDigits,
  isDecimal,
  bankingNumber,
  ledger,
  accountNumber,
} from '@utils/validators';

import { Box } from 'components/Box';
import { IntegrationField } from 'components/IntegrationField';
import { IntegrationHeader } from 'components/IntegrationHeader';
import { useModal } from 'features/modals';
import { DokobitOnboarding } from 'integrations/dokobit/OnboardingPage';
import { EloomiOnboarding } from 'integrations/eloomi/OnboardingPage';
import { EloomiMonitorOnboarding } from 'integrations/eloomiMonitor/OnboardingPage';
import { H3coreOnboarding } from 'integrations/H3core/OnboardingPage';
import { LearnWorldsOnboarding } from 'integrations/learnWorlds/onboarding-page';
import { OnboardingFormOnboarding } from 'integrations/onboardingForm/OnboardingPage';
import { OneCRMOnboardingPage } from 'integrations/onecrm/onboarding-page';
import { ReykjavikOnboarding } from 'integrations/reykjavik/onboarding-page';
import { ReykjavikSAP } from 'integrations/reykjavik/sap-page';
import { RUOnboarding } from 'integrations/ru/onboarding-page';
import { SAPOnboarding } from 'integrations/SAP/OnboardingPage';
import { SignetOnboarding } from 'integrations/signet/OnboardingPage';
import { SiminnPayOnboarding } from 'integrations/siminnPay/OnboardingPage';
import { TaktikalOnboarding } from 'integrations/taktikal/OnboardingPage';
import { TeachableOnboarding } from 'integrations/teachable/OnboardingPage';
import { IntegrationData } from 'models/integrations';
import { useStore } from 'models/Provider';

import { File, RepeatingContainer } from './styles';

function Integrations({ integration }) {
  const { t, api } = useStore();
  const { showFullscreenModal } = useModal();
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);

  async function preview() {
    setSaving(true);
    if (integration.isValid()) {
      const previewUrl = await integration.previewForApplication();
      const data = IntegrationData.create({ value: previewUrl }, { api });
      showFullscreenModal('INTEGRATION_FILE_PREVIEW', { data });
    } else {
      const firstError = integration.firstError();
      if (firstError && firstError.name) {
        const el = document.querySelector(`[data-id=${firstError.name}]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    setSaving(false);
  }

  async function submit(retrigger, save) {
    setSaving(true);
    if (save || integration.isValid()) {
      await integration.submitForApplication(retrigger, save);
    } else {
      const firstError = integration.firstError();
      if (firstError && firstError.name) {
        const el = document.querySelector(`[data-id=${firstError.name}]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    setSaving(false);
  }

  async function remove() {
    setSaving(true);
    await integration.deleteForApplication();
    setSaving(false);
  }

  function renderTriggeredInput(data) {
    switch (data.type) {
      case 'hidden':
        return null;
      case 'boolean':
        return (
          <Switch
            defaultValue={data.value}
            id={data.name}
            name={data.name}
            isDisabled
            label={data.label}
          />
        );
      case 'checkbox':
        return (
          <Stack space={1} orientation="horizontal">
            {data.value ? <IconCheckboxChecked /> : <IconCheckboxIdle />}
            <FormLabel text={data.label} />
          </Stack>
        );
      case 'file':
        return (
          <Stack space={1}>
            <FormLabel text={data.label} />
            <File onClick={() => showFullscreenModal('INTEGRATION_FILE_PREVIEW', { data })}>
              <Stack orientation="horizontal" space={2} align="center">
                <IconOpen color="purple50" />
                <Text color="purple100" type="semibold14" text={data.fileName} />
              </Stack>
            </File>
          </Stack>
        );
      case 'fileurl':
        return (
          <Stack space={1}>
            <FormLabel text={data.label} />
            <a href={data.value} target="_blank" rel="noopener noreferrer">
              <File>
                <Stack orientation="horizontal" space={2} align="center">
                  <IconOpen color="purple50" />
                  <Text color="purple100" type="semibold14" text={data.fileName} />
                </Stack>
              </File>
            </a>
          </Stack>
        );
      case 'url':
        return (
          <Stack space={1}>
            <FormLabel text={data.label} />
            <a href={data.value} target="_blank" rel="noopener noreferrer">
              {data.value}
            </a>
          </Stack>
        );
      case 'separator':
        return <hr />;
      case 'header':
        return <Text font="bold16" color="purple100" text={data.value} />;
      case 'subtext':
        return <Text font="regular12" style={{ fontStyle: 'italic' }} text={data.value} />;
      case 'text':
        return <TextArea label={data.label} name={data.name} value={data.value} readOnly />;
      case 'alert':
        return <Alert variant={data.alertType ?? 'error'}>{data.value}</Alert>;
      default:
        return <TextField label={data.label} name={data.name} value={data.inputValue} isDisabled />;
    }
  }

  function validateItem(value, data) {
    setValidating(true);
    data.validators.every((v) => {
      switch (v) {
        case 'ssn':
          if (data.value && !isValidSsn(data.value)) {
            data.setError(t('integration.validators.ssn'));
            return false;
          }
          data.setError('');
          return true;
        case 'number':
          if (data.value && !isOnlyDigits(data.value)) {
            data.setError(t('integration.validators.number'));
            return false;
          }
          data.setError('');
          return true;
        case 'decimal':
          if (data.value && !isDecimal(data.value)) {
            data.setError(t('integration.validators.decimal'));
            return false;
          }
          data.setError('');
          return true;
        case 'bank-number':
          if (data.value && !bankingNumber(data.value)) {
            data.setError(t('integration.validators.bankNumber'));
            return false;
          }
          data.setError('');
          return true;
        case 'ledger':
          if (data.value && !ledger(data.value)) {
            data.setError(t('integration.validators.ledger'));
            return false;
          }
          data.setError('');
          return true;
        case 'account-number':
          if (data.value && !accountNumber(data.value)) {
            data.setError(t('integration.validators.accountNumber'));
            return false;
          }
          data.setError('');
          return true;
        default:
          data.setError('');
          return true;
      }
    });
    setValidating(false);
  }

  if (integration.name === 'OneCRM') {
    return <OneCRMOnboardingPage />;
  } else if (integration.name === 'H3core') {
    return (
      <H3coreOnboarding
        saving={saving}
        submit={submit}
        integration={integration}
        validator={validateItem}
        validating={validating}
      />
    );
  } else if (integration.name === 'SAP') {
    return (
      <SAPOnboarding
        saving={saving}
        submit={submit}
        integration={integration}
        validator={validateItem}
        validating={validating}
      />
    );
  } else if (integration.name === 'Eloomi') {
    return (
      <EloomiOnboarding
        saving={saving}
        submit={submit}
        integration={integration}
        validator={validateItem}
        validating={validating}
        remove={remove}
      />
    );
  } else if (integration.name === 'Eloomi Monitor') {
    return <EloomiMonitorOnboarding integration={integration} />;
  } else if (integration.name === 'Síminn Pay') {
    return (
      <SiminnPayOnboarding
        saving={saving}
        submit={submit}
        integration={integration}
        validator={validateItem}
        validating={validating}
        remove={remove}
      />
    );
  } else if (integration.name === 'Teachable') {
    return (
      <TeachableOnboarding
        saving={saving}
        submit={submit}
        integration={integration}
        validator={validateItem}
        validating={validating}
        remove={remove}
      />
    );
  } else if (integration.name === 'Signet') {
    return <SignetOnboarding base={integration} validator={validateItem} />;
  } else if (integration.name === 'Taktikal') {
    return <TaktikalOnboarding base={integration} validator={validateItem} />;
  } else if (integration.name === 'Dokobit') {
    return (
      <DokobitOnboarding applicationId={String(integration.parent.id)} validator={validateItem} />
    );
  } else if (integration.name === 'Ráðningarsamningur') {
    return (
      <ReykjavikOnboarding applicationId={String(integration.parent.id)} validator={validateItem} />
    );
  } else if (integration.name === 'Reykjavík SAP') {
    return <ReykjavikSAP applicationId={String(integration.parent.id)} validator={validateItem} />;
  } else if (integration.name === 'Onboarding Form') {
    return (
      <OnboardingFormOnboarding
        applicationId={String(integration.parent.id)}
        validator={validateItem}
      />
    );
  } else if (integration.name === 'Avia') {
    return <LearnWorldsOnboarding applicationId={String(integration.parent.id)} />;
  } else if (integration.name === 'Finish Contract') {
    return (
      <RUOnboarding
        integration={integration}
        validateItem={validateItem}
        validating={validating}
        remove={remove}
        preview={preview}
        renderTriggeredInput={renderTriggeredInput}
      />
    );
  }

  integration.loadForApplication();
  return (
    <>
      <IntegrationHeader integration={integration} />
      <Box mt="24">
        {saving && <Spinner />}
        {integration.refetching && <Spinner />}
        {(integration.state === 'loading' || integration.refetching) && <Spinner />}
        {integration.state === 'loaded' && (
          <>
            <Stack space={4}>
              {integration.triggered &&
                !integration.canRetrigger &&
                integration.data.map((data, i) =>
                  data.type === 'repeating' ? (
                    <Stack space={1} key={i}>
                      <FormLabel text={data.label} />
                      {data.fields.map((fieldSet, idx) => (
                        <RepeatingContainer key={idx}>
                          {fieldSet.map((fs, fsIdx) => (
                            <div key={fsIdx}>{renderTriggeredInput(fs)}</div>
                          ))}
                        </RepeatingContainer>
                      ))}
                    </Stack>
                  ) : (
                    <Fragment key={i}>{renderTriggeredInput(data)}</Fragment>
                  )
                )}

              {(!integration.triggered || (integration.triggered && integration.canRetrigger)) &&
                integration.data.map((data, i) => (
                  <IntegrationField
                    data={data}
                    refetch={integration.refetchForApplication}
                    validator={validateItem}
                    isDisabled={saving || integration.refetching}
                    key={i}
                  />
                ))}
            </Stack>

            <Box flex justifyContent="flex-end" mt="20">
              {integration.triggered && integration.canRetrigger && (
                <Box ml="8">
                  <Button
                    isDisabled={saving}
                    onClick={() => submit(true, false)}
                    text={t('application.onboarding.integration.resend')}
                  />
                </Box>
              )}
              {integration.canDelete && integration.triggered && (
                <Box ml="8">
                  <Button
                    variant="primary-red"
                    isDisabled={saving}
                    onClick={remove}
                    text={t('application.onboarding.integration.delete')}
                  />
                </Box>
              )}
              {!integration.triggered && (
                <>
                  {integration.canPreview && (
                    <Box ml="8">
                      <IconButton
                        icon={<IconVisibilityShown />}
                        disabled={
                          saving || integration.refetching || validating || integration.hasErrors()
                        }
                        onClick={() => preview()}
                      />
                    </Box>
                  )}
                  <Box ml="8">
                    <Button
                      variant="primary-blue"
                      isDisabled={saving || integration.refetching}
                      onClick={() => submit(false, true)}
                      text={t('application.onboarding.integration.save')}
                    />
                  </Box>
                  <Box ml="8">
                    <Button
                      isDisabled={
                        saving ||
                        integration.refetching ||
                        integration.isBlocked ||
                        validating ||
                        integration.hasErrors()
                      }
                      onClick={() => submit(false, false)}
                      text={t('application.onboarding.integration.submit')}
                    />
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

export default observer(Integrations);
